import type { MilestoneMap1 } from '@mm-shared/types/MilestoneMap1';
import type { ImplementationPlanColumn, Objective1 } from '@mm-shared/types/Objective1';
import { getStatusCellState } from '../../utils/statusUtils';
import { implicitRank } from './fractionalRank';
import { intervalContaining, isCardDone, latestTimelineStatus } from './cardStatus';
import type { CardStatusInterval } from './cardStatus';
import type { PlanCard, PlanColumnDef } from '../types';

/**
 * planLayout — card derivation and column geometry for the Implementation Plans
 * board (ADR §1c, §2).
 *
 * Everything here is a pure function of map state. Nothing in this view measures
 * the DOM: the canvas and every node get explicit dimensions computed here, because
 * the board mounts inside a `hidden` named-view sibling where ResizeObserver and
 * ReactFlow's node measurement both read 0x0 (ADR §2 "Canvas sizing").
 */

export const PLAN_COLUMNS: readonly PlanColumnDef[] = [
  { id: 'now', label: 'Now' },
  { id: 'next', label: 'Next' },
  { id: 'someday', label: 'Someday/Maybe' },
] as const;

// Column geometry. Flow coordinates equal screen coordinates (zoom pinned to 1).
export const COLUMN_WIDTH = 320;
export const COLUMN_GAP = 16;
export const COLUMN_HEADER_HEIGHT = 48;
export const COLUMN_PADDING = 12;
export const MIN_COLUMN_HEIGHT = 480;

// Card geometry. Height is computed from persisted card data — never measured.
export const CARD_WIDTH = COLUMN_WIDTH - 2 * COLUMN_PADDING;
export const CARD_GAP = 12;

/* ---------------------------------------------------------------------------
 * The card height budget (ADR A1c) — normative, not descriptive.
 *
 * A1-R1: every vertical block inside a card renders with (1) an inline `height`
 * whose value is one of these constants, (2) an inline `flexShrink: 0`, and
 * (3) a `data-block="<name>"` attribute. No block is intrinsically sized and no
 * block may absorb an over-budget neighbour by shrinking. The formula is
 * normative; the DOM conforms to it, not the reverse.
 *
 * Every value below is a BORDER-BOX value — Tailwind preflight sets
 * `box-sizing: border-box` globally (`src/index.css`, `@tailwind base`) — so the
 * budget has to pay for the card's own border and padding. Omitting them is
 * finding B-1; under-budgeting the rendered content is v1 defect L1. Both are
 * closed here by construction, and by the two guards in A6:
 * `__tests__/ObjectiveStickyNoteNode.test.tsx` binds these constants to the
 * markup's inline pins, and the E2E budget guard measures the rendered CSS
 * against those pins in a real browser.
 * ------------------------------------------------------------------------- */

/** The card root's `border`, uniform on all four sides. */
export const CARD_BORDER = 1;
/** `p-3` on the card root, uniform on all four sides (no `sm:` variant). */
export const CARD_PADDING = 12;
/**
 * The card's own border + padding overhead. Both are uniform, so the same
 * constant is the card's vertical *and* horizontal overhead — used by the height
 * budget here and by the footer width budget in A5a/D1.
 */
export const CARD_CHROME = 2 * (CARD_BORDER + CARD_PADDING);

/**
 * Catalyst's `outline-2` + `outline-offset-2` focus treatment paints up to 4px
 * beyond a control's box (`components_catalyst/badge.tsx`). A non-clipping block
 * that wraps such a control budgets this instead of clipping it (A1-R1b, WCAG
 * 2.4.7) — an outline is clipped by an *ancestor's* overflow, never its own.
 */
export const FOCUS_RING_CLEARANCE = 4;

/** Catalyst `Badge` box: `py-0.5` + a `/5` (20px) line box at either breakpoint. */
export const EYEBROW_BADGE_HEIGHT = 24;
/** Pinned block, no clip. The clearance doubles as the gap to the title. */
export const EYEBROW_BLOCK_HEIGHT = EYEBROW_BADGE_HEIGHT + 2 * FOCUS_RING_CLEARANCE;

export const TITLE_CLAMP_LINES = 2;
/** `text-sm/5` — no `sm:` variant, so this term is breakpoint-independent. */
export const TITLE_LINE_HEIGHT = 20;
/** Pinned block, self-clipping: the title element *is* the control (A1-R1b). */
export const TITLE_BLOCK_HEIGHT = TITLE_CLAMP_LINES * TITLE_LINE_HEIGHT;

export const INFO_CLAMP_LINES = 2;
/** `text-xs/4`. */
export const INFO_LINE_HEIGHT = 16;
/** The info block's leading gap, paid inside its own pinned box (A1b). */
export const INFO_BLOCK_GAP = 4;
/** Pinned block, self-clipping, and **conditional** on `additionalInfo`. */
export const INFO_BLOCK_HEIGHT = INFO_BLOCK_GAP + INFO_CLAMP_LINES * INFO_LINE_HEIGHT;

/** One key-result `<li>`. */
export const KEY_RESULT_ROW_BOX_HEIGHT = 20;
/** `space-y-1` between rows. */
export const KEY_RESULT_ROW_GAP = 4;
/** The `<ul>`'s leading gap — the one *external* margin in the card (A1b). */
export const KEY_RESULT_LIST_MARGIN_TOP = 4;
/** The per-key-result term of `cardHeight`. */
export const KEY_RESULT_ROW_HEIGHT = KEY_RESULT_ROW_BOX_HEIGHT + KEY_RESULT_ROW_GAP;

/** The Catalyst `Select`'s pinned vertical padding — Catalyst's own `sm:` value. */
export const FOOTER_SELECT_PAD_Y = 5;
/** `text-base/6` and `sm:text-sm/6` are both `/6`, so this is breakpoint-independent. */
export const FOOTER_CONTROL_LINE_HEIGHT = 24;
export const FOOTER_SELECT_BORDER_Y = 2;
/** Pinned block, no clip: the `Select` is the tallest control in the row. */
export const FOOTER_HEIGHT =
  2 * FOOTER_SELECT_PAD_Y + FOOTER_CONTROL_LINE_HEIGHT + FOOTER_SELECT_BORDER_Y;

/**
 * The base card: chrome + the three unconditional blocks. Defined as the sum of
 * its named terms, never as a literal, so a change to any block is
 * arithmetically forced into `cardHeight`.
 */
export const CARD_BASE_HEIGHT =
  CARD_CHROME + EYEBROW_BLOCK_HEIGHT + TITLE_BLOCK_HEIGHT + FOOTER_HEIGHT;

/* ---------------------------------------------------------------------------
 * The footer's horizontal budget (ADR A5a/D1) — the tightest constraint in the
 * card. Catalyst's `Select` is responsive on padding-inline and font-size as
 * well as height (finding B2), so every term below is pinned inline on the
 * rendered `<select>`; that is the only way the label-fit budget can be a number
 * rather than a hope.
 * ------------------------------------------------------------------------- */

/** One icon button: `p-0.5` (4) + `h-4 w-4` (16). */
export const FOOTER_ICON_CONTROL_SIZE = 20;
/** `gap-1` between footer children. */
export const FOOTER_GAP = 4;
/** `theme(spacing.3) - 1` — Catalyst's `sm:` value, pinned inline. */
export const FOOTER_SELECT_PAD_X_START = 11;
/** `theme(spacing.9) - 1` — clears the chevron span, which needs 20 + 8 = 28. */
export const FOOTER_SELECT_PAD_X_END = 35;
export const FOOTER_SELECT_BORDER_X = 2;
/** Catalyst's `sm:text-sm` value, pinned inline at every breakpoint. */
export const FOOTER_SELECT_FONT_SIZE = 14;
/**
 * The sizing `<div>` around the `Select`, pinned by inline `width` — *not* by a
 * `w-*` class, which reaches only Catalyst's wrapper `<span>` where `w-full`
 * wins on emission order (finding M-1). It is the whole of the row's remaining
 * width: content width (270) less four icon controls and four gaps (96). The
 * fifth footer child (the details trigger, A4b-i) lands with `additionalInfo`;
 * the budget reserves its slot from here so the row never has to be re-derived.
 */
export const FOOTER_SELECT_WIDTH =
  CARD_WIDTH - CARD_CHROME - (4 * FOOTER_ICON_CONTROL_SIZE + 4 * FOOTER_GAP);

/* ---------------------------------------------------------------------------
 * Collapse: the board's session-only view state (ADR B1, B2, B5).
 *
 * This is the first thing in this subsystem whose height depends on something
 * that is not in Firestore. It is therefore modelled the same way persisted
 * data is: a card is projected through the collapse set exactly once, and every
 * layout and drop function downstream consumes the projection — so there is no
 * path by which a collapsed height reaches the renderer but not the drop
 * resolver (ADR B2).
 * ------------------------------------------------------------------------- */

/**
 * A card's view state on this board, in this tab, right now (ADR B1). Exactly
 * two states: Map3Relatives' third level (`fullyCollapsed`) folds a whole map
 * into a line within a stack of maps, and a sticky note has no third rung.
 */
export type PlanCardCollapse = 'expanded' | 'titleOnly';

/**
 * A `PlanCard` projected through the board's session-only **view** state —
 * both of them (ADR B1, ADR.ImplementationPlansUX2 C5a).
 *
 * `PlanCard` describes only persisted data and `deriveCards` still returns it;
 * `PlanCardView` is the board's *view* of a card and is what every layout and
 * drop function consumes. Keeping the two distinct is what stops "which column
 * is this card in" (Firestore) and "is this card collapsed" (this session, this
 * tab) from being the same kind of thing (ADR B1).
 *
 * `hidden` joins `collapse` on the SAME projection rather than getting a
 * parallel one (C5a): B2's invariant is that a card is projected through view
 * state exactly once, so no path exists by which a view-modified card reaches
 * the renderer but not the drop resolver. A second projection function for
 * visibility would be that invariant's first exception.
 */
export type PlanCardView = PlanCard & {
  collapse: PlanCardCollapse;
  /**
   * Whether the board is currently filtering this card out (C4a/C5a). A hidden
   * card is skipped by `computeLayout`'s positioning loop, emits no node, and
   * therefore has no geometry — but it keeps its place in the **rank** axis
   * (`columnCards`), which is what C5c ranks against.
   */
  hidden: boolean;
};

/**
 * The board's per-column hide-done filter (ADR.ImplementationPlansUX2 C4a):
 * one boolean per column, exactly like its two cluster neighbours
 * (expand-all/collapse-all) — never a single board-wide flag.
 */
export type HideDone = Record<ImplementationPlanColumn, boolean>;

/**
 * The taller of the collapsed row's two occupants: the title's `text-sm/5` line
 * box (20) and an icon button's box (`p-0.5` + `h-4 w-4`, 20). `Math.max` is
 * not decoration — the row has to hold both, so if either grows the row grows.
 */
export const COLLAPSED_ROW_BOX_HEIGHT = Math.max(TITLE_LINE_HEIGHT, FOOTER_ICON_CONTROL_SIZE);
/**
 * Pinned block, no clip: it wraps the card's view controls, so it budgets their
 * focus rings rather than clipping them (A1-R1b). The clearance is symmetric,
 * exactly as the eyebrow's is.
 */
export const COLLAPSED_BLOCK_HEIGHT = COLLAPSED_ROW_BOX_HEIGHT + 2 * FOCUS_RING_CLEARANCE;
/** The whole collapsed card, border box (ADR B2). */
export const CARD_COLLAPSED_HEIGHT = CARD_CHROME + COLLAPSED_BLOCK_HEIGHT;

export function columnX(columnIndex: number): number {
  return columnIndex * (COLUMN_WIDTH + COLUMN_GAP);
}

/**
 * The key-result `<ul>`'s own box, excluding its top margin (A1b):
 * `marginTop (4) + height (24n - 4) = 24n`, exactly the term `cardHeight` adds
 * per key result. The `n === 0` branch is not decoration — without it the
 * function returns -4, React emits `height: -4px`, the browser drops it as
 * invalid, and the one block in the card is left intrinsically sized with an
 * unbudgeted 4px margin.
 */
export function keyResultsListHeight(n: number): number {
  return n === 0 ? 0 : n * KEY_RESULT_ROW_HEIGHT - KEY_RESULT_LIST_MARGIN_TOP;
}

/**
 * Whether a card renders (and budgets) the conditional `info` block — the one
 * expression, shared by `computeLayout` and the card so the height and the DOM
 * cannot disagree. Deliberately *not* a `PlanCard` field: it would duplicate
 * state `additionalInfo` already carries (ADR A1d).
 */
export function cardHasAdditionalInfo(card: Pick<PlanCard, 'additionalInfo'>): boolean {
  return Boolean(card.additionalInfo?.trim());
}

/**
 * The card's border-box height (ADR A1d, B2). A pure function of persisted card
 * data plus the card's collapse state: edit state, `canEdit` and `shortId`
 * presence are all still deliberately not arguments (A1d, A1e — an
 * edit-state-dependent height would have to be lifted into `computeLayout` and
 * would rebuild the whole nodes array per keystroke).
 *
 * The third argument is a **view** state the user chose, deliberately
 * height-changing, and it does not weaken INV-13: no in-progress edit is an
 * argument here before or after this round. A collapsed card's height is
 * content-independent by construction — the early return sees neither of the
 * other two arguments.
 */
export function cardHeight(
  keyResultCount: number,
  hasAdditionalInfo: boolean,
  collapse: PlanCardCollapse,
): number {
  if (collapse === 'titleOnly') return CARD_COLLAPSED_HEIGHT;
  return (
    CARD_BASE_HEIGHT +
    (hasAdditionalInfo ? INFO_BLOCK_HEIGHT : 0) +
    keyResultCount * KEY_RESULT_ROW_HEIGHT
  );
}

/**
 * Derives the board's cards from the map document and the objectives collection
 * (ADR §1c/§1e):
 * - membership and implicit-rank ordering come from milestoneMap.objectiveUuids
 * - a uuid with no loaded objective is skipped
 * - placement is read only from implementationPlan[map.uuid]; placements keyed to
 *   other maps are ignored
 * - unassigned cards default to the 'now' column at their implicit rank (never written)
 *
 * **The colour primitives (ADR.ImplementationPlansUX2 C1c).** Each card also
 * carries the four inputs the grid's colour composition takes, and the two
 * booleans are the **grid cell's**, not the objective's: the latest
 * calendar-timeline status is located in the interval **containing** it at the
 * map's base grain (`iv.start < d <= iv.end`, the grid's own rule), and
 * `getStatusCellState` is asked about *that* interval. Reading them off the
 * whole objective diverges on ordinary data — key results dated Q4 with a Q2
 * status give the grid `bg-status-g` and the card `bg-status-G`.
 *
 * `intervals` is a **required** third parameter, built once per map by the
 * wrapper (`cardStatusIntervals`). Required rather than optional because an
 * optional one lets a forgetting caller compile and ship uncoloured cards;
 * built by the caller because building it here would regenerate hundreds of
 * `Date`s on every objective edit. An empty array (or a status outside every
 * interval) yields `false`/`false` — never the map's last interval.
 */
export function deriveCards(
  milestoneMap: Pick<MilestoneMap1, 'uuid' | 'objectiveUuids' | 'parentObjectiveUuids'>,
  objectives: Array<Objective1>,
  intervals: readonly CardStatusInterval[],
): PlanCard[] {
  const byUuid = new Map(objectives.map((o) => [o.uuid, o]));
  const uuids = milestoneMap.objectiveUuids ?? [];
  const n = uuids.length;
  // One Set per derivation, not `isParentInMap`'s Array.includes per card —
  // that is O(cards x parents) inside a useMemo the whole board depends on.
  const parentUuids = new Set(milestoneMap.parentObjectiveUuids ?? []);

  const cards: PlanCard[] = [];
  uuids.forEach((uuid, i) => {
    const objective = byUuid.get(uuid);
    if (!objective) return;

    const latest = latestTimelineStatus(objective.statuses ?? []);
    const interval = latest
      ? intervalContaining(intervals, new Date(latest.forIntervalEnding))
      : undefined;
    const { hasResults, hasKeyResults } = interval
      ? getStatusCellState(objective, interval)
      : { hasResults: false, hasKeyResults: false };

    const placement = objective.implementationPlan?.[milestoneMap.uuid];
    cards.push({
      uuid,
      // Both coerce to '' rather than undefined (invariant P1): the card type
      // promises strings, and cardLayoutKey encodes them (ADR A1f).
      shortId: objective.shortId ?? '',
      text: objective.text,
      additionalInfo: objective.additionalInfo ?? '',
      column: placement?.column ?? 'now',
      effectiveRank: placement?.rank ?? implicitRank(i, n),
      hasStoredRank: placement?.rank !== undefined,
      // `delivered` travels with each row (ADR.ImplementationPlansUX2 C2a/C2d)
      // so the card can render it as a checkbox. Coerced with Boolean(...),
      // not trusted as already-a-boolean: Result1.delivered is typed required
      // but the type is not enforced at the Firestore boundary (the theme this
      // whole module defends against).
      keyResults: (objective.results ?? [])
        .filter((r) => r.isKeyResult)
        .map((r) => ({ uuid: r.uuid, text: r.text, delivered: Boolean(r.delivered) })),
      statusCode: latest ? latest.statusCode : null,
      hasResults,
      hasKeyResults,
      objectiveIsParent: parentUuids.has(uuid),
    });
  });
  return cards;
}

/**
 * Projects the derived cards through **all** of the board's session-only view
 * state (ADR B1, ADR.ImplementationPlansUX2 C5a) — the single point at which
 * view state joins persisted data. Formerly `projectCollapse`; renamed rather
 * than joined by a second function, because B2's invariant is that a card is
 * projected exactly once and a parallel visibility projection would be its
 * first exception.
 *
 * **Collapse.** Membership *is* the state: with exactly two states,
 * `'expanded'` needs no entry, and a card that arrives from a collaborator
 * after a collapse-all is absent from the set and therefore expanded, which is
 * the correct default (B9). A stale entry for a deleted objective is a set
 * member nothing ever looks up.
 *
 * **Visibility.** `hidden = hideDone[card.column] && isCardDone(card)` —
 * per-column (C4a), and computed from the card's own key results so "what you
 * see decides whether it disappears" (C3).
 *
 * **`frozenHidden` REPLACES that rule rather than patching it** (C6c). When a
 * drag is in flight the canvas passes the snapshot of hidden uuids taken at
 * drag start, and from then until the drop **no** card's `hidden` can move —
 * not for a remote `delivered` flip, not for a local `hideDone` toggle. That
 * is what keeps `resolveDrop` running on the geometry the user actually saw:
 * a mid-drag reflow of the *siblings* resolves the gesture against positions
 * that were never rendered. An **empty** snapshot is meaningful and is passed
 * like any other — it pins the empty set, which is precisely what stops a
 * remote completion hiding a sibling on a `hideDone`-on column where nothing
 * was done yet.
 *
 * Memoize at the call site: everything downstream is identity-sensitive — the
 * canvas's `layout` memo and the `useCallback` identity of `onNodesChange`,
 * whose dependency list starts with the cards array (ADR B1).
 */
export function projectView(
  cards: PlanCard[],
  collapsed: ReadonlySet<string>,
  hideDone: HideDone,
  frozenHidden?: ReadonlySet<string>,
): PlanCardView[] {
  return cards.map(
    (c): PlanCardView => ({
      ...c,
      collapse: collapsed.has(c.uuid) ? 'titleOnly' : 'expanded',
      hidden: frozenHidden
        ? frozenHidden.has(c.uuid)
        : hideDone[c.column] && isCardDone(c),
    }),
  );
}

/**
 * The board's **view** rebuild key (ADR B3), the sibling of `boardLayoutKey`'s
 * **data** key. Sorted so a set with the same members always yields the same
 * string regardless of insertion order — which is what makes a redundant
 * toggle a genuine no-op: a key that does not change does not re-run the
 * rebuild effect.
 *
 * `Array.from` rather than a spread: this package targets ES5 without
 * `downlevelIteration`, so spreading a `Set` does not type-check. Same result.
 */
export function collapseKey(collapsed: ReadonlySet<string>): string {
  return Array.from(collapsed).sort().join('|');
}

/**
 * The board's **second** view rebuild key (ADR.ImplementationPlansUX2 C6a),
 * the sibling of `collapseKey`. A `Record` rebuilt per render has the same
 * problem a `Set` has — no stable primitive identity — so the rebuild effect
 * needs a string here too; the fixed three-column domain makes that string
 * **three characters** rather than a join over an unbounded set.
 *
 * Written with an explicit `'1'`/`'0'` rather than `+boolean` so the encoding
 * is one obvious literal per column and the key can never widen accidentally.
 */
export function hideDoneKey(hideDone: HideDone): string {
  const bit = (on: boolean) => (on ? '1' : '0');
  return `${bit(hideDone.now)}${bit(hideDone.next)}${bit(hideDone.someday)}`;
}

/**
 * Sorts a column's cards by the total order (effectiveRank, uuid) — ADR §1c.
 * Plain lexicographic comparison: R1 makes it numerically faithful, and the uuid
 * tiebreak makes display deterministic and convergent across clients.
 *
 * Takes and returns `PlanCardView` (ADR B2): its return **is** what populates
 * `BoardLayout.columnCards`, so a narrower signature here would re-erase
 * `collapse` one line after `computeLayout` widens it.
 */
export function sortColumn(cards: PlanCardView[]): PlanCardView[] {
  return [...cards].sort((a, b) => {
    if (a.effectiveRank !== b.effectiveRank) {
      return a.effectiveRank < b.effectiveRank ? -1 : 1;
    }
    return a.uuid < b.uuid ? -1 : 1;
  });
}

export type CardPosition = {
  card: PlanCardView;
  x: number;
  y: number;
  width: number;
  height: number;
};

export type ColumnPosition = {
  column: PlanColumnDef;
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * **Three arrays, three meanings** (ADR.ImplementationPlansUX2 C5b). They are
 * equal whenever nothing is hidden, and each is the *only* right answer to its
 * own question the moment something is:
 *
 * | Array | Axis | Question it answers |
 * |---|---|---|
 * | `columnCards[id]` | **rank** | where does a rank sit in the column's persisted order? |
 * | `visibleColumnCards[id]` | **visible** | what is the user looking at, in what order? |
 * | `cards` | **geometry** | where on the canvas is each rendered card? |
 */
export type BoardLayout = {
  columns: ColumnPosition[];
  /**
   * `CardPosition[]` for **visible cards only** — the geometry axis. A hidden
   * card is skipped by the positioning loop, contributes zero height, and the
   * cards below it move up as if it were absent. Because no node is emitted
   * for it, B6b's interleaved node order is preserved verbatim.
   */
  cards: CardPosition[];
  canvasWidth: number;
  canvasHeight: number;
  /**
   * The column's **full** sorted order by (effectiveRank, uuid), hidden cards
   * included — the **rank** axis (C5b). This is what C5c step 3 translates a
   * visible gap into, so that a drop between two visible cards with hidden
   * cards between them produces a rank that is right on screen *and* right in
   * the data.
   */
  columnCards: Record<ImplementationPlanColumn, PlanCardView[]>;
  /**
   * The same order minus hidden cards — the **visible** axis (C5b). Neither of
   * the other two can stand in for it: `columnCards` includes cards the user
   * cannot see (so a no-op guard against it discards real gestures and admits
   * phantom ones — C5c step 0), and `cards` is positions, not an order.
   */
  visibleColumnCards: Record<ImplementationPlanColumn, PlanCardView[]>;
};

/**
 * Computes every node's position and dimensions plus the canvas size (ADR §2).
 *
 * Takes `PlanCardView[]` (ADR B2): heights are read per card from its own
 * collapse state, and mixed-height columns are already the norm, so no drop
 * rule changes — `resolveDrop` compares each sibling's own midpoint and has
 * done so since a card's height started varying with key-result count.
 *
 * Hidden cards (C5b) contribute **zero** height and no `CardPosition`, but
 * keep their place in `columnCards`. `columnHeight` and `canvasHeight` are
 * unchanged expressions — they simply see a shorter running `y`.
 */
export function computeLayout(cards: PlanCardView[]): BoardLayout {
  const columnCards = {
    now: sortColumn(cards.filter((c) => c.column === 'now')),
    next: sortColumn(cards.filter((c) => c.column === 'next')),
    someday: sortColumn(cards.filter((c) => c.column === 'someday')),
  } satisfies Record<ImplementationPlanColumn, PlanCardView[]>;

  const visibleColumnCards = {
    now: columnCards.now.filter((c) => !c.hidden),
    next: columnCards.next.filter((c) => !c.hidden),
    someday: columnCards.someday.filter((c) => !c.hidden),
  } satisfies Record<ImplementationPlanColumn, PlanCardView[]>;

  const cardPositions: CardPosition[] = [];
  let tallestContent = 0;

  PLAN_COLUMNS.forEach((column, columnIndex) => {
    const x = columnX(columnIndex) + COLUMN_PADDING;
    let y = COLUMN_HEADER_HEIGHT + CARD_GAP;
    for (const card of columnCards[column.id]) {
      // A hidden card contributes zero height and no position (C5b): `y` does
      // not advance, so the cards below take the positions they would have had
      // if it were absent. The loop still walks the FULL order, which is what
      // keeps `columnCards` and this loop reading the same array.
      if (card.hidden) continue;
      const height = cardHeight(
        card.keyResults.length,
        cardHasAdditionalInfo(card),
        card.collapse,
      );
      cardPositions.push({ card, x, y, width: CARD_WIDTH, height });
      y += height + CARD_GAP;
    }
    tallestContent = Math.max(tallestContent, y);
  });

  const columnHeight = Math.max(MIN_COLUMN_HEIGHT, tallestContent + COLUMN_PADDING);
  const columns: ColumnPosition[] = PLAN_COLUMNS.map((column, columnIndex) => ({
    column,
    x: columnX(columnIndex),
    y: 0,
    width: COLUMN_WIDTH,
    height: columnHeight,
  }));

  return {
    columns,
    cards: cardPositions,
    canvasWidth: columnX(PLAN_COLUMNS.length - 1) + COLUMN_WIDTH,
    canvasHeight: columnHeight,
    columnCards,
    visibleColumnCards,
  };
}

/**
 * Maps an x coordinate to the nearest column by center (ADR §2 drop semantics).
 * Used by the commit-on-drop handler (mr-kv9w.5); pure and tested here.
 */
export function columnFromX(x: number): ImplementationPlanColumn {
  let nearest: ImplementationPlanColumn = PLAN_COLUMNS[0].id;
  let nearestDistance = Infinity;
  PLAN_COLUMNS.forEach((column, columnIndex) => {
    const center = columnX(columnIndex) + COLUMN_WIDTH / 2;
    const distance = Math.abs(x + CARD_WIDTH / 2 - center);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearest = column.id;
    }
  });
  return nearest;
}

/**
 * The per-card layout key fragment (ADR §2 "Reconciliation with remote echoes",
 * A1f). Every field `node.data` carries is in the key: key-result *text* and
 * *delivered* state (ADR.ImplementationPlansUX2 C2d) — a collaborator's
 * checkbox toggle leaves the row count unchanged and must still repaint, and
 * `card.keyResults` is stringified whole below, so this is free — no separate
 * rebuild key was added for it (C6a);
 * `hasStoredRank`, because in the §1d string-equal case a stored rank can
 * replace an implicit one without changing `effectiveRank`; and `shortId` +
 * `additionalInfo`, for the same reason (PR #5 review finding — `node.data`
 * must never go stale relative to `cards`).
 *
 * The encoding is `JSON.stringify`, not a `:`-join: `additionalInfo` is
 * free-form prose that routinely contains `:` and newlines, which makes
 * collisions constructible — `(text: 'x', additionalInfo: 'y')` and
 * `(text: 'x:y', additionalInfo: '')` would produce the same fragment, so a
 * simultaneous title/info edit that swapped one for the other would leave the
 * card unrepainted. The key is only ever compared for equality, never parsed.
 *
 * The four **colour primitives** join the key for the same reason
 * (ADR.ImplementationPlansUX2 C1c/C1e): the nodes array has exactly one rebuild
 * path, so a field outside every rebuild key would never repaint — a
 * collaborator's status change, or a key-result **deadline** edit that moves a
 * result into or out of the latest status's interval, would leave the card
 * wearing the old colour. The rebuild they trigger recomputes arithmetically
 * identical positions: colour is paint, never geometry.
 */
export function cardLayoutKey(card: PlanCard): string {
  return JSON.stringify([
    card.uuid,
    card.shortId,
    card.text,
    card.additionalInfo,
    card.column,
    card.effectiveRank,
    card.hasStoredRank,
    card.keyResults,
    card.statusCode,
    card.hasResults,
    card.hasKeyResults,
    card.objectiveIsParent,
  ]);
}

/** The board-level layout key: rebuild local node state only when this changes. */
export function boardLayoutKey(cards: PlanCard[]): string {
  return cards.map(cardLayoutKey).join('||');
}
